import { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  MobHeader,
  MobIconButton,
  MobOfflineBanner,
  MobTabBar,
  type TabDef,
} from '../../components/mob';
import { IconBuffet, IconKitchen, IconMore, IconOrders, IconRefresh, IconTables, IconUser } from '../../components/shared/Icons';
import { refreshKeyForPath } from '../../api/queryKeys';
import { useCanBuffet } from '../../auth/useCan';
import { useBuffetToday } from '../../hooks/useBuffet';
import { useKots } from '../../hooks/useKots';
import { usePrintAlerts } from '../../hooks/usePrintAlerts';
import { useOrders } from '../../hooks/useOrders';
import { useOutlet } from '../../hooks/useOutlet';
import { ShellProvider, useShellState } from './ShellContext';
import { tokenStore } from '../../auth/tokenStore';
import './AppShell.css';

export const OUTLET_NAME_KEY = 'wtr.outletName';

const TAB_TITLE: Array<[prefix: string, title: string]> = [
  ['/tables', 'Tables'],
  ['/orders', 'Open orders'],
  ['/kitchen', 'Kitchen'],
  ['/buffet', 'Buffet'],
  ['/more', 'Account'],
];

function defaultTitle(pathname: string): string {
  return TAB_TITLE.find(([p]) => pathname.startsWith(p))?.[1] ?? 'DineOps';
}

function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** Header refresh: invalidates the current route's query prefix. */
function HeaderRefreshButton() {
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const key = refreshKeyForPath(pathname);
  const isFetching = useIsFetching({ queryKey: key ?? ['__no_refresh__'], exact: false });
  const refresh = useCallback(() => {
    if (key) void qc.invalidateQueries({ queryKey: key });
  }, [key, qc]);
  if (!key) return null;
  return (
    <MobIconButton ariaLabel={isFetching > 0 ? 'Refreshing' : 'Refresh'} spinning={isFetching > 0} onClick={refresh}>
      <IconRefresh />
    </MobIconButton>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { header, actions, focused } = useShellState();
  const { data: outlet } = useOutlet();

  // Badges: open orders, and READY tickets. Same keys as the screens use, so
  // the shell and a screen never double-poll.
  const { data: orders } = useOrders('wait');
  const { data: kots } = useKots({ status: 'live' });
  // Every screen: a ticket this waiter sent or opened that turns FAILED alerts once.
  usePrintAlerts(kots);
  const openCount = orders?.length ?? 0;
  const readyCount = useMemo(() => (kots ?? []).filter((k) => k.kotStatus === 'READY').length, [kots]);
  // Buffet tab: only for a user who may see the board, at an outlet with a buffet meal today.
  const canBuffet = useCanBuffet('view');
  const { data: buffetToday } = useBuffetToday(canBuffet);
  const hasBuffet = canBuffet && (buffetToday?.meals.length ?? 0) > 0;

  // Remember the outlet name for the login card ("Waiter · Terrace Grill").
  useEffect(() => {
    if (outlet?.outletName) {
      try {
        localStorage.setItem(OUTLET_NAME_KEY, outlet.outletName);
      } catch {
        // ignore
      }
    }
  }, [outlet?.outletName]);

  const tabs: TabDef[] = useMemo(
    () => [
      // The Room Service outlet has no floor: the first tab is its room orders.
      { to: '/tables', label: outlet?.isRoomService ? 'Rooms' : 'Tables', icon: <IconTables />, match: (p) => p.startsWith('/tables') },
      {
        to: '/orders',
        label: 'Orders',
        icon: <IconOrders />,
        badge: openCount,
        match: (p) => p.startsWith('/orders'),
      },
      { to: '/kitchen', label: 'Kitchen', icon: <IconKitchen />, badge: readyCount, match: (p) => p.startsWith('/kitchen') },
      ...(hasBuffet
        ? [{ to: '/buffet', label: 'Buffet', icon: <IconBuffet />, match: (p: string) => p.startsWith('/buffet') }]
        : []),
      { to: '/more', label: 'More', icon: <IconMore />, match: (p) => p.startsWith('/more') },
    ],
    [openCount, readyCount, outlet?.isRoomService, hasBuffet],
  );

  const title = header?.title ?? (outlet?.isRoomService && pathname.startsWith('/tables') ? 'Room service' : defaultTitle(pathname));
  // The waiter's section follows the outlet in the header (spec 2.41 §12.1a).
  const section = tokenStore.getSectionName();
  const subtitle =
    header?.subtitle ?? `${outlet?.outletName ?? 'DineOps'}${section ? ` · ${section}` : ''} · ${todayLabel()}`;

  return (
    <div className="app-shell">
      <MobOfflineBanner />
      <MobHeader
        title={title}
        subtitle={subtitle}
        showPropertyChevron={!!header?.chevron}
        onPropertyClick={header?.chevron?.onClick}
        right={
          <div className="app-shell__header-right">
            <HeaderRefreshButton />
            <MobIconButton ariaLabel="Account" onClick={() => navigate('/more')}>
              <IconUser />
            </MobIconButton>
          </div>
        }
      />

      <main className="app-shell__main">
        <Outlet />
      </main>

      {actions ? <div className="app-shell__actions">{actions}</div> : null}
      {!focused ? <MobTabBar tabs={tabs} /> : null}
    </div>
  );
}

export function AppShell() {
  return (
    <ShellProvider>
      <Shell />
    </ShellProvider>
  );
}
